import { ref, set, onValue, off, push, update, increment, onDisconnect, get, serverTimestamp } from 'firebase/database';
import { database } from '../config/firebase';
import { auth } from '../config/firebase';

class FirebaseService {
    constructor() {
        this.database = database;
        this.gameListeners = new Map();
        this.disconnectHandlers = new Map();
        this.setupConnectionMonitoring();
    }

    setupConnectionMonitoring() {
        const connectedRef = ref(this.database, '.info/connected');
        onValue(connectedRef, (snap) => {
            if (snap.val() === true) {
                console.log('Connected to Firebase');
                // When we reconnect, check all active games and update status
                this.gameListeners.forEach((_, roomId) => {
                    this.handleReconnect(roomId);
                });
            } else {
                console.log('Disconnected from Firebase');
                // Handle all active games when disconnected
                this.gameListeners.forEach((_, roomId) => {
                    this.handleDisconnect(roomId, auth.currentUser?.uid);
                });
            }
        });
    }

    async handleReconnect(roomId) {
        const currentUserId = auth.currentUser?.uid;
        if (!currentUserId) return;

        try {
            const gameRef = ref(this.database, `games/${roomId}`);
            const gameSnapshot = await get(gameRef);
            const gameData = gameSnapshot.val();

            const playerRef = ref(this.database, `games/${roomId}/players/${currentUserId}`);
            const playerSnapshot = await get(playerRef);
            const playerData = playerSnapshot.val();

            if (playerData) {
                console.log(`Reconnecting player ${currentUserId} in game ${roomId}`);

                // Update player status
                await update(playerRef, {
                    active: true,
                    lastActive: null
                });

                // If this player was the prompt giver and the game is in 'generating' or 'waiting' state,
                // ensure we maintain their prompt giver status
                if (gameData?.promptGiver?.id === currentUserId &&
                    (gameData.status === 'waiting' || gameData.status === 'generating')) {
                    await update(gameRef, {
                        promptGiver: {
                            id: currentUserId,
                            name: playerData.name
                        },
                        status: 'waiting'
                    });
                }

                // Set up new disconnect handler
                const onDisconnectRef = onDisconnect(playerRef);
                await onDisconnectRef.update({
                    active: false,
                    lastActive: serverTimestamp()
                });
                this.disconnectHandlers.set(roomId, onDisconnectRef);
            }
        } catch (error) {
            console.error('Error handling reconnect:', error);
        }
    }

    // Join or create a game room
    async joinGame(roomId, player) {
        try {
            const gameRef = ref(this.database, `games/${roomId}`);
            const playerRef = ref(this.database, `games/${roomId}/players/${player.id}`);
            const onDisconnectRef = onDisconnect(playerRef);

            // Check if player already exists
            const existingPlayerSnapshot = await get(playerRef);
            const existingPlayer = existingPlayerSnapshot.val();

            // Prepare player data, preserving existing data if any
            const playerData = {
                ...(existingPlayer || {}),
                ...player,
                active: true,
                lastActive: null
            };

            // Set player data
            await set(playerRef, playerData);

            // Set up disconnect handler
            await onDisconnectRef.update({
                active: false,
                lastActive: serverTimestamp()
            });

            // Store disconnect handler reference for cleanup
            this.disconnectHandlers.set(roomId, onDisconnectRef);

            // Set up beforeunload handler
            const handleBeforeUnload = async () => {
                try {
                    await this.handleDisconnect(roomId, player.id);
                } catch (error) {
                    console.error('Error handling disconnect:', error);
                }
            };

            window.addEventListener('beforeunload', handleBeforeUnload);
            this.disconnectHandlers.set(`${roomId}_unload`, handleBeforeUnload);

            return true;
        } catch (error) {
            console.error('Error joining game:', error);
            return false;
        }
    }

    // Listen to game updates
    subscribeToGame(roomId, callback) {
        const gameRef = ref(this.database, `games/${roomId}`);

        const listener = onValue(gameRef, async (snapshot) => {
            const gameData = snapshot.val();
            if (gameData) {
                // Check for inactive players
                if (gameData.players) {
                    const inactivePlayers = Object.entries(gameData.players)
                        .filter(([_, player]) => !player.active)
                        .map(([id, player]) => ({
                            id,
                            name: player.name,
                            lastActive: player.lastActive
                        }));

                    if (inactivePlayers.length > 0) {
                        console.log('Inactive players:', inactivePlayers);
                    }
                }

                // Check if there are any active players
                const hasActivePlayers = Object.values(gameData.players || {}).some(player => player.active);
                const isPendingCleanup = gameData.status === 'pending_cleanup';

                if (!hasActivePlayers) {
                    if (isPendingCleanup) {
                        const cleanupTime = new Date(gameData.cleanupScheduledAt + 60000); // cleanup time is 1 minute after scheduled
                        const timeLeft = Math.max(0, cleanupTime - Date.now());
                        console.log(`Game will be cleaned up in ${Math.ceil(timeLeft / 1000)} seconds`);
                    }
                    console.log('No active players, cleaning up game');
                    await this.cleanup(roomId);
                    callback(null);
                } else {
                    callback(gameData);
                }
            } else {
                console.log('Game not found or removed');
                await this.cleanup(roomId);
                callback(null);
            }
        });

        this.gameListeners.set(roomId, listener);
    }

    // Unsubscribe from game updates
    unsubscribeFromGame(roomId) {
        const gameRef = ref(this.database, `games/${roomId}`);
        off(gameRef);
        this.gameListeners.delete(roomId);

        // Cancel disconnect handler
        const disconnectHandler = this.disconnectHandlers.get(roomId);
        if (disconnectHandler) {
            disconnectHandler.cancel();
            this.disconnectHandlers.delete(roomId);
        }
    }

    // Update game settings
    async updateSettings(roomId, settings) {
        try {
            console.log('FirebaseService: Updating settings for room', roomId, 'with', settings);
            const settingsRef = ref(this.database, `games/${roomId}/settings`);
            await set(settingsRef, settings);
            console.log('FirebaseService: Settings updated successfully');
            return true;
        } catch (error) {
            console.error('FirebaseService: Error updating settings:', error);
            throw error;
        }
    }

    // Submit a prompt
    async submitPrompt(roomId, prompt) {
        const promptRef = ref(this.database, `games/${roomId}/currentPrompt`);
        const statusRef = ref(this.database, `games/${roomId}/status`);

        await update(ref(this.database, `games/${roomId}`), {
            currentPrompt: prompt,
            status: 'generating'
        });
    }

    // Submit a guess
    async submitGuess(roomId, playerId, guess) {
        const guessRef = ref(this.database, `games/${roomId}/guesses`);
        const newGuessRef = push(guessRef);

        await set(newGuessRef, {
            playerId,
            guess,
            timestamp: Date.now()
        });
    }

    // Move to next round
    async nextRound(roomId) {
        const gameRef = ref(this.database, `games/${roomId}`);

        // Get current game state
        const snapshot = await get(gameRef);
        const game = snapshot.val();

        if (!game || !game.players) {
            throw new Error('Game not found or no players');
        }

        // Convert players object to array and sort by join time or ID to maintain consistent order
        const playersList = Object.entries(game.players)
            .filter(([_, player]) => player.active) // Only include active players
            .map(([id, player]) => ({
                id,
                ...player
            }));

        if (playersList.length === 0) {
            throw new Error('No active players available');
        }

        // Find current prompt giver's index
        const currentPromptGiverIndex = playersList.findIndex(
            player => player.id === game.promptGiver?.id
        );

        // Get next prompt giver (cycle back to start if at end)
        const nextPromptGiverIndex = (currentPromptGiverIndex + 1) % playersList.length;
        const nextPromptGiver = playersList[nextPromptGiverIndex];

        // Update game state with new round and prompt giver
        await update(gameRef, {
            status: 'waiting',
            currentPrompt: null,
            generatedImage: null,
            guesses: null,
            isPromptRevealed: false,
            currentRound: increment(1),
            promptGiver: {
                id: nextPromptGiver.id,
                name: nextPromptGiver.name
            }
        });
    }

    // Reveal prompt
    async revealPrompt(roomId) {
        const gameRef = ref(this.database, `games/${roomId}`);

        await update(gameRef, {
            isPromptRevealed: true
        });
    }

    // Handle player disconnect
    async handleDisconnect(roomId, playerId) {
        if (!roomId || !playerId) return;

        try {
            const playerRef = ref(this.database, `games/${roomId}/players/${playerId}`);
            console.log(`Marking player ${playerId} as inactive in game ${roomId}`);

            // Update player status
            await update(playerRef, {
                active: false,
                lastActive: serverTimestamp()
            });

            // Check if this was the last active player
            const gameRef = ref(this.database, `games/${roomId}`);
            const snapshot = await get(gameRef);
            const gameData = snapshot.val();

            if (gameData && gameData.players) {
                const hasOtherActivePlayers = Object.entries(gameData.players)
                    .some(([id, player]) => id !== playerId && player.active);

                if (!hasOtherActivePlayers) {
                    console.log('No active players remaining, game will be cleaned up in 1 minute');
                }
            }
        } catch (error) {
            console.error('Error handling disconnect:', error);
        }
    }

    // Clean up resources
    async cleanup(roomId) {
        if (!roomId) return;

        try {
            // Get current user ID
            const currentUserId = auth.currentUser?.uid;
            if (currentUserId) {
                // Set player as inactive before cleanup
                const playerRef = ref(this.database, `games/${roomId}/players/${currentUserId}`);
                await update(playerRef, {
                    active: false,
                    lastActive: serverTimestamp()
                });
            }

            // Remove beforeunload handler
            const unloadHandler = this.disconnectHandlers.get(`${roomId}_unload`);
            if (unloadHandler) {
                window.removeEventListener('beforeunload', unloadHandler);
                this.disconnectHandlers.delete(`${roomId}_unload`);
            }

            // Cancel disconnect handler
            const disconnectHandler = this.disconnectHandlers.get(roomId);
            if (disconnectHandler) {
                await disconnectHandler.cancel();
                this.disconnectHandlers.delete(roomId);
            }

            // Unsubscribe from game updates
            const gameRef = ref(this.database, `games/${roomId}`);
            off(gameRef);
            this.gameListeners.delete(roomId);

        } catch (error) {
            console.error('Error during cleanup:', error);
        }
    }
}

export default new FirebaseService(); 